// ─────────────────────────────────────────────────────────────────────────────
// Projects
// ─────────────────────────────────────────────────────────────────────────────
// These mirror BACKEND/seed_portfolio.sql, so the site shows a full portfolio
// before the CMS has been seeded and if the API is ever unreachable. Once the
// admin dashboard has projects in it, the live API takes over — see
// hooks/useProjects.js.
//
// Descriptions state only what is visible in the photographs. No client names,
// no street locations, no dates, no budgets, no invented square footage —
// none of that has been supplied, and a portfolio that quietly makes it up is
// worse than one that's brief.
//
// `location` is deliberately absent for the same reason: the business serves
// Durham Region, but which project sat in which city has not been confirmed,
// and tagging a photo "Whitby" for SEO when nobody has verified it is exactly
// the sort of thing that gets a local listing penalised.

export const PROJECTS = [
  {
    id: "bathroom-tiled-shower-vanity",
    title: "Bathroom Renovation with Tiled Shower and Vanity",
    category: "Bath",
    serviceSlug: "bath",
    summary:
      "Marble-look porcelain, a glass enclosure on a hexagon mosaic base, and a backlit round mirror over a quartz-top vanity.",
    description:
      "A full bathroom renovation finished in large-format marble-look porcelain, with a glass shower enclosure set on a hexagon mosaic base, a backlit round mirror, and a quartz-top vanity. The shower walls and floor were waterproofed and set before any of the finish work went in.",
    images: ["bathGlassDoor", "bathVanityMirror", "bathMarbleDetail"],
    featured: true,
    // Used by the homepage featured slot: a display headline that reads as a
    // sentence, and the trades actually visible in these photographs.
    featuredHeadline: "A bathroom taken back to the studs",
    trades: "Waterproofing, tile, plumbing, carpentry",
  },
  {
    id: "basement-bedroom-hallway",
    title: "Finished Basement with Bedroom and Hallway",
    category: "Legal Basement",
    serviceSlug: "legal-basement",
    summary:
      "Taken from bare framing to finished living space — insulated and vapour-sealed, drywalled, trimmed and floored.",
    description:
      "A basement build-out taken from bare framing through to finished living space: insulated and vapour-sealed walls, drywall, trim and doors, and vinyl plank flooring carried through the bedroom and the connecting hallway.",
    images: ["basementHallway", "basementBedroom", "insulationFraming"],
    featured: true,
  },
  {
    id: "staircase-refinishing",
    title: "Staircase Refinishing and Repaint",
    category: "Painting",
    serviceSlug: "painting",
    summary:
      "A carpeted staircase stripped to bare treads, then rebuilt with stained wood over painted risers and panelled walls.",
    description:
      "An existing staircase stripped back to bare treads, then rebuilt with stained wood treads over painted white risers and panelled walls. The surrounding area was fully masked and dust-controlled for the length of the job.",
    images: ["staircaseAfter", "staircaseBefore", "staircaseInProgress", "staircaseMasking"],
    featured: true,
  },
  {
    id: "large-format-tile-flooring",
    title: "Large-Format Tile Flooring",
    category: "Tile",
    serviceSlug: "tile",
    summary:
      "Marble-look porcelain laid over a levelled subfloor and carried through an entryway and landing.",
    description:
      "Marble-look porcelain floor tile laid over a prepared and levelled subfloor, carried through an entryway and landing. Levelling clips were used throughout to keep the surface flat across the large-format pieces.",
    images: ["tileFloorLargeFormat", "tileEntryway", "tileLevelingClips"],
    featured: false,
  },
  {
    id: "marble-feature-wall",
    title: "Marble Feature Wall with Slat-Wood Accent",
    category: "Tile",
    serviceSlug: "tile",
    summary:
      "Book-matched porcelain slabs paired with a vertical slat-wood panel and a recessed reveal at the base.",
    description:
      "A living-area feature wall clad in book-matched marble-look porcelain slabs, paired with a vertical slat-wood accent panel and a recessed linear opening running along the base.",
    images: ["tileFeatureWall"],
    featured: false,
  },
  {
    id: "vinyl-plank-flooring",
    title: "Vinyl Plank Flooring Installation",
    category: "Flooring",
    serviceSlug: "flooring",
    summary:
      "Grey vinyl plank run wall to wall through a finished lower level, with new baseboard fitted after.",
    description:
      "Grey vinyl plank flooring installed wall to wall in a finished lower level, with new baseboard and trim fitted once the floor was down.",
    images: ["flooringVinylPlank"],
    featured: false,
  },
  {
    id: "exterior-front-garage-doors",
    title: "Exterior Refresh — Front Door and Garage Doors",
    category: "Exterior Reno",
    serviceSlug: "exterior-reno",
    summary:
      "Front door, garage doors, bay window surround and gable trim refinished in black against existing red brick.",
    description:
      "An exterior update on a two-storey brick home: the front door, garage doors, bay window surround and gable trim were all refinished in black for a cleaner, more current look against the existing brick.",
    images: ["exteriorBrickGarage"],
    featured: false,
  },
  {
    id: "laundry-nook",
    title: "Laundry Nook Build-Out",
    category: "Renovations",
    serviceSlug: "renovations",
    summary:
      "A stacked washer and dryer fitted into a purpose-built nook, tiled to match the adjoining bathroom.",
    description:
      "A stacked washer and dryer fitted into a purpose-built laundry nook, finished with marble-look tile flooring and trim carried through from the adjoining bathroom.",
    images: ["laundryStacked"],
    featured: false,
  },
];

// Homepage "featured project" slot.
//
// The bathroom, not the staircase: the staircase already carries the
// before/after section a little further down the homepage, and showing the
// same job twice in one scroll makes a portfolio of eight look like a
// portfolio of two. The bathroom also has the strongest sequence of finished
// photography — the enclosure, the vanity wall, and the tile detail.
export const FEATURED_PROJECT_ID = "bathroom-tiled-shower-vanity";

// ── Before / after ──────────────────────────────────────────────────────────
// Only pair photographs that are demonstrably the same space. These two are
// the same staircase from the same corner — the panelled wall, the run of the
// stringer and the tile floor at the bottom all line up. If a future pair
// can't be verified that way, leave it out rather than implying a transform
// nobody can check.
export const BEFORE_AFTER = {
  projectId: "staircase-refinishing",
  before: "staircaseBefore",
  after: "staircaseAfter",
  beforeLabel: "Before",
  afterLabel: "After",
  title: "One staircase, start to finish",
  description:
    "Bare pine treads and a temporary handrail, through to stained treads, painted risers and new iron spindles. Same corner of the same house — drag the handle to compare.",
};

export const getProjectById = (id) => PROJECTS.find((project) => project.id === id);

export const FEATURED_PROJECTS = PROJECTS.filter((project) => project.featured);
